import { v4 as uuidv4 } from 'uuid';
import { MAX_STEPS_PER_PROFILE } from '@/constants/EditMaxes.ts';
import type { ProfileConfig } from '@/types/edit/ProfileConfig.ts';
import { EditLimitReachedError } from '@/errors/edit/EditLimitReachedError.ts';
import { ConfigNotFoundError } from '@/errors/edit/ConfigNotFoundError.ts';
import type { StepConfig } from '@/types/edit/StepConfig.ts';
import type { PromptConfig } from '@/types/edit/PromptConfig.ts';
import { PromptConfigService } from '@/services/config/PromptConfigService.ts';
import { NameInUseError } from '@/errors/edit/NameInUseError.ts';
import { ValidationFailedError } from '@/errors/edit/ValidationFailedError.ts';
import { usePromptStore } from '@/stores/PromptStore.ts';
import { DefaultStepConfigs } from '@/constants/DefaultStepConfigs.ts';
import { logger } from '@/lib/logger.ts';

export abstract class StepConfigService {
    public static createStep(
        profileConfigs: Record<ProfileConfig['id'], ProfileConfig>,
        stepConfigs: Record<StepConfig['id'], StepConfig>,
        promptConfigs: Record<PromptConfig['id'], PromptConfig>,
        profileConfigId: ProfileConfig['id'],
    ): {
        profileConfigs: Record<ProfileConfig['id'], ProfileConfig>;
        stepConfigs: Record<StepConfig['id'], StepConfig>;
        promptConfigs: Record<PromptConfig['id'], PromptConfig>;
        newStepConfigId: StepConfig['id'];
    } {
        const profileStepConfigs = Object.values(stepConfigs).filter(
            (s) => s.profileId === profileConfigId,
        );
        if (Object.values(profileStepConfigs).length >= MAX_STEPS_PER_PROFILE) {
            throw new EditLimitReachedError('Max steps reached');
        }

        const newStepConfigId = uuidv4();

        stepConfigs = {
            ...stepConfigs,
            [newStepConfigId]: {
                id: newStepConfigId,
                profileId: profileConfigId,
                promptIds: [],
                name: 'Enter Step Name',
                order: profileStepConfigs.length,
                editable: true,
            },
        };

        // Update profile config
        profileConfigs = {
            ...profileConfigs,
            [profileConfigId]: {
                ...profileConfigs[profileConfigId],
                stepIds: [...profileConfigs[profileConfigId].stepIds, newStepConfigId],
            },
        };

        return {
            profileConfigs,
            stepConfigs,
            promptConfigs,
            newStepConfigId,
        };
    }

    public static getAllStepConfigs = (): Record<StepConfig['id'], StepConfig> => {
        const promptStore = usePromptStore();

        return {
            ...DefaultStepConfigs,
            ...promptStore.stepConfigs,
        };
    };

    public static getCustomStepConfigs = (): Record<StepConfig['id'], StepConfig> => {
        const promptStore = usePromptStore();

        return promptStore.stepConfigs;
    };

    public static getStepConfig = (id: StepConfig['id']): StepConfig => {
        return this.getAllStepConfigs()[id];
    };

    public static moveStep(
        profileConfigs: Record<ProfileConfig['id'], ProfileConfig>,
        stepConfigs: Record<StepConfig['id'], StepConfig>,
        profileId: ProfileConfig['id'],
        stepId: StepConfig['id'],
        direction: 'up' | 'down',
    ): {
        stepConfigs: Record<StepConfig['id'], StepConfig>;
    } {
        const profileConfig = profileConfigs[profileId];
        if (!profileConfig) {
            throw new ConfigNotFoundError(`Profile config with id ${profileId} not found`);
        }

        const profileStepConfigs = Object.fromEntries(
            Object.entries(stepConfigs).filter(([key]) => profileConfig.stepIds.includes(key)),
        );

        const updatedStepConfigs = StepConfigService.calculateNewStepsOrder(
            profileStepConfigs,
            stepId,
            direction,
        );

        stepConfigs = {
            ...stepConfigs,
            ...updatedStepConfigs,
        };

        return {
            stepConfigs,
        };
    }

    public static updateStepName(
        stepConfigs: Record<StepConfig['id'], StepConfig>,
        stepConfigId: StepConfig['id'],
        name: StepConfig['name'],
    ): {
        stepConfigs: Record<StepConfig['id'], StepConfig>;
    } {
        const stepConfig = stepConfigs[stepConfigId];
        if (!stepConfig) {
            throw new ConfigNotFoundError(`Step config with id ${stepConfigId} not found`);
        }

        if (name.length === 0 || name.length > 50) {
            throw new ValidationFailedError('Step name must be between 1 and 50 characters long');
        }

        const otherProfileStepConfigs = Object.values(stepConfigs).filter(
            (s) => s.profileId === stepConfig.profileId && s.id !== stepConfigId,
        );
        if (
            Object.values(otherProfileStepConfigs).find(
                (p) => p.profileId === stepConfig.id && p.name.toLowerCase() === name.toLowerCase(),
            )
        ) {
            throw new NameInUseError(
                `The step name ${name} is already being used by another step in this profile.`,
            );
        }

        stepConfigs = {
            ...stepConfigs,
            [stepConfigId]: {
                ...stepConfigs[stepConfigId],
                name: name,
            },
        };

        return {
            stepConfigs,
        };
    }

    public static updateStepConfigs(stepConfigs: Record<StepConfig['id'], StepConfig>): void {
        const filteredConfigs = Object.fromEntries(
            Object.entries(stepConfigs).filter(([, config]) => config.editable),
        );
        const promptStore = usePromptStore();
        promptStore.setStepConfigs(filteredConfigs);
    }

    public static deleteStep(
        profileConfigs: Record<ProfileConfig['id'], ProfileConfig>,
        stepConfigs: Record<StepConfig['id'], StepConfig>,
        promptConfigs: Record<PromptConfig['id'], PromptConfig>,
        stepConfigId: StepConfig['id'],
    ): {
        profileConfigs: Record<ProfileConfig['id'], ProfileConfig>;
        stepConfigs: Record<StepConfig['id'], StepConfig>;
        promptConfigs: Record<PromptConfig['id'], PromptConfig>;
    } {
        const stepConfig = stepConfigs[stepConfigId];
        if (!stepConfig) {
            throw new ConfigNotFoundError(`Step config with id ${stepConfigId} not found`);
        }

        // Delete prompts
        const stepPromptConfigs = Object.values(promptConfigs).filter(
            (p) => p.stepId === stepConfigId,
        );
        for (const promptConfig of stepPromptConfigs) {
            const {
                profileConfigs: promptUpdatedProfileConfigs,
                stepConfigs: promptUpdatedStepConfigs,
                promptConfigs: promptUpdatedPromptConfigs,
            } = PromptConfigService.deletePrompt(
                profileConfigs,
                stepConfigs,
                promptConfigs,
                promptConfig.id,
            );
            profileConfigs = promptUpdatedProfileConfigs;
            stepConfigs = promptUpdatedStepConfigs;
            promptConfigs = promptUpdatedPromptConfigs;
        }

        // Delete step config
        const { [stepConfigId]: _, ...unorderedUpdatedStepConfigData } = stepConfigs;

        // Re-order remaining steps
        const orderedProfileStepConfigs = Object.values(unorderedUpdatedStepConfigData)
            .filter((config) => config.profileId === stepConfig.profileId)
            .sort((a, b) => a.order - b.order)
            .map((config, index) => ({
                ...config,
                order: index,
            }));

        const otherStepConfigs = Object.values(unorderedUpdatedStepConfigData).filter(
            (config) => config.profileId !== stepConfig.profileId,
        );

        const orderedStepConfigs = [...orderedProfileStepConfigs, ...otherStepConfigs];

        stepConfigs = Object.fromEntries(orderedStepConfigs.map((config) => [config.id, config]));

        // Update profile
        const stepProfileConfigs = Object.values(profileConfigs).filter((p) =>
            p.stepIds.includes(stepConfigId),
        );
        for (const profileConfig of stepProfileConfigs) {
            profileConfigs = {
                ...profileConfigs,
                [profileConfig.id]: {
                    ...profileConfigs[profileConfig.id],
                    stepIds: profileConfigs[profileConfig.id].stepIds.filter(
                        (i) => i !== stepConfigId,
                    ),
                },
            };
        }

        return {
            profileConfigs,
            stepConfigs,
            promptConfigs,
        };
    }

    public static validate(stepConfigs: Record<StepConfig['id'], StepConfig>): string[] {
        const errors: string[] = [];

        for (const stepConfig of Object.values(stepConfigs)) {
            // Name
            if (stepConfig.name.length === 0) {
                errors.push('Step name cannot be empty');
            } else if (stepConfig.name.length > 50) {
                errors.push('Step name cannot be longer than 50 characters');
            }
        }

        return errors;
    }

    private static calculateNewStepsOrder = (
        stepConfigs: Record<string, StepConfig>,
        stepConfigId: string,
        direction: 'up' | 'down',
    ): Record<string, StepConfig> => {
        const configToUpdate = stepConfigs[stepConfigId];
        if (!configToUpdate) {
            logger.error('StepConfig not found');
            return stepConfigs;
        }

        const keys = Object.keys(stepConfigs);
        const order = configToUpdate.order;
        let swapIndex = -1;

        if (direction === 'up' && order > 0) {
            swapIndex = order - 1;
        } else if (direction === 'down' && order < keys.length - 1) {
            swapIndex = order + 1;
        }

        if (swapIndex !== -1) {
            const configToSwap = Object.values(stepConfigs).find((p) => p.order === swapIndex);
            if (!configToSwap) return stepConfigs;

            const tempOrder = configToUpdate.order;
            configToUpdate.order = configToSwap.order;
            configToSwap.order = tempOrder;
        }

        return Object.fromEntries(
            Object.entries(stepConfigs).sort(([, a], [, b]) => a.order - b.order),
        );
    };
}
